import React from 'react';
import { CustomerService } from '@/services/customerService';
import { ProductService } from '@/services/productService';
import { UserService } from '@/services/userService';
import { BillService } from '@/services/invoiceService';
import { 
  ManagementConfig, 
  TableColumn,
  TableAction,
  ManagementFormField
} from '@/models/management';
import { 
  Customer, 
  CreateCustomerData, 
  UpdateCustomerData
} from '@/models/customer';
import { 
  Product, 
  CreateProductData,
  UpdateProductData
} from '@/models/product';
import { 
  User, 
  CreateUserData,
  UpdateUserData
} from '@/models/user';
import {
  Invoice,
  CreateInvoiceData,
  UpdateInvoiceData
} from '@/models/invoice';

// Customer Management Configuration
export const customerManagementConfig: ManagementConfig<Customer, CreateCustomerData, UpdateCustomerData> = {
  entityName: 'Customer',
  entityNamePlural: 'Customers',
  routePath: '/customers',
  
  service: {
    getItems: CustomerService.getCustomers,
    getItem: async (id: string) => ({ success: true, data: {} as Customer }),
    createItem: CustomerService.createCustomer,
    updateItem: CustomerService.updateCustomer,
    deleteItem: async (id: string) => {
      const response = await CustomerService.deleteCustomer(id);
      return { success: response.success, data: { deletedCount: 1 } };
    },
    bulkDeleteItems: CustomerService.bulkDeleteCustomers,
    exportItems: CustomerService.exportCustomers
  },
  
  title: 'Customer Management',
  subtitle: 'Manage your customer database with full CRUD operations',
  
  defaultSortBy: 'name',
  defaultSortOrder: 'asc',
  
  // Search fields for client-side filtering
  searchFields: ['name', 'address_1', 'tel', 'mobile', 'gst_id', 'panno'],
  
  columns: [
    {
      id: 'name',
      label: 'Customer Name',
      minWidth: 200,
      sortable: true
    },
    {
      id: 'address_1',
      label: 'Address Line 1',
      minWidth: 200,
      format: (value: any) => {
        if (!value) return '-';
        return value.length > 40 ? `${value.substring(0, 40)}...` : value;
      }
    },
    {
      id: 'pin',
      label: 'PIN Code',
      minWidth: 100,
      align: 'center',
      sortable: true
    },
    {
      id: 'tel',
      label: 'Telephone',
      minWidth: 120,
      align: 'center',
      format: (value: any) => value ? `+91 ${value}` : '-'
    },
    {
      id: 'mobile',
      label: 'Mobile',
      minWidth: 120,
      align: 'center',
      format: (value: any) => value ? `+91 ${value}` : '-'
    },
    {
      id: 'gst_id',
      label: 'GST Number',
      minWidth: 140,
      format: (value: any) => value || '-'
    },
    {
      id: 'panno',
      label: 'PAN Number',
      minWidth: 120,
      format: (value: any) => value || '-'
    },
    {
      id: 'localoutside',
      label: 'GST Type',
      minWidth: 100,
      align: 'center',
      format: (value: any) => {
        const color = value === 'Local' ? 'primary' : 'secondary';
        return { label: value, color, size: 'small', className: 'status-chip' };
      }
    },
    {
      id: 'updated_on',
      label: 'Last Updated',
      minWidth: 120,
      align: 'center',
      format: (value: any) => {
        if (!value) return '-';
        return new Date(value).toLocaleDateString();
      }
    }
  ] as TableColumn<Customer>[],
  
  actions: [
    {
      label: 'View',
      icon: 'ViewIcon',
      onClick: () => {},
      color: 'info'
    },
    {
      label: 'Edit',
      icon: 'EditIcon',
      onClick: () => {},
      color: 'primary'
    },
    {
      label: 'Delete',
      icon: 'DeleteIcon',
      onClick: () => {},
      color: 'error'
    }
  ] as TableAction<Customer>[],
  
  createFormFields: [
    {
      name: 'name',
      label: 'Customer Name',
      type: 'text',
      required: true,
      gridSize: { xs: 12, sm: 6 },
      placeholder: 'Enter customer name'
    },
    {
      name: 'localoutside',
      label: 'GST Type',
      type: 'select',
      required: true,
      gridSize: { xs: 12, sm: 6 },
      options: [
        { value: 'Local', label: 'Local' },
        { value: 'Outside', label: 'Outside' }
      ]
    },
    {
      name: 'panno',
      label: 'PAN Number',
      type: 'uppercase',
      required: true,
      gridSize: { xs: 12, sm: 6 },
      placeholder: 'ABCDE1234F',
      validation: {
        pattern: '^[A-Z]{5}[0-9]{4}[A-Z]{1}$',
        message: 'PAN number must be in format: ABCDE1234F'
      }
    },
    {
      name: 'gst_id',
      label: 'GST Number',
      type: 'uppercase',
      required: true,
      gridSize: { xs: 12, sm: 6 },
      placeholder: '22ABCDE1234F1Z5',
      validation: {
        pattern: '^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$',
        message: 'GST number must be in valid format'
      }
    }
  ] as ManagementFormField[],
  
  editFormFields: [
    {
      name: 'name',
      label: 'Customer Name',
      type: 'text',
      required: true,
      gridSize: { xs: 12, sm: 6 },
      placeholder: 'Enter customer name'
    },
    {
      name: 'localoutside',
      label: 'GST Type',
      type: 'select',
      required: true,
      gridSize: { xs: 12, sm: 6 },
      options: [
        { value: 'Local', label: 'Local' },
        { value: 'Outside', label: 'Outside' }
      ]
    },
    {
      name: 'address_1',
      label: 'Address Line 1',
      type: 'text',
      required: true,
      gridSize: { xs: 12 },
      placeholder: 'Enter address line 1'
    },
    {
      name: 'address_2',
      label: 'Address Line 2',
      type: 'text',
      required: false,
      gridSize: { xs: 12 },
      placeholder: 'Enter address line 2'
    },
    {
      name: 'address_3',
      label: 'Address Line 3',
      type: 'text',
      required: false,
      gridSize: { xs: 12 },
      placeholder: 'Enter address line 3'
    },
    {
      name: 'address_4',
      label: 'Address Line 4',
      type: 'text',
      required: false,
      gridSize: { xs: 12 },
      placeholder: 'Enter address line 4'
    },
    {
      name: 'pin',
      label: 'PIN Code',
      type: 'text',
      required: true,
      gridSize: { xs: 12, sm: 6 },
      placeholder: 'Enter PIN code'
    },
    {
      name: 'tel',
      label: 'Telephone',
      type: 'number',
      required: true,
      gridSize: { xs: 12, sm: 6 },
      placeholder: 'Enter telephone number'
    },
    {
      name: 'fax',
      label: 'Fax',
      type: 'number',
      required: false,
      gridSize: { xs: 12, sm: 6 },
      placeholder: 'Enter fax number'
    },
    {
      name: 'mobile',
      label: 'Mobile',
      type: 'number',
      required: true,
      gridSize: { xs: 12, sm: 6 },
      placeholder: 'Enter mobile number'
    },
    {
      name: 'panno',
      label: 'PAN Number',
      type: 'uppercase',
      required: true,
      gridSize: { xs: 12, sm: 6 },
      placeholder: 'ABCDE1234F',
      validation: {
        pattern: '^[A-Z]{5}[0-9]{4}[A-Z]{1}$',
        message: 'PAN number must be in format: ABCDE1234F'
      }
    },
    {
      name: 'gst_id',
      label: 'GST Number',
      type: 'uppercase',
      required: true,
      gridSize: { xs: 12, sm: 6 },
      placeholder: '22ABCDE1234F1Z5',
      validation: {
        pattern: '^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$',
        message: 'GST number must be in valid format'
      }
    }
  ] as ManagementFormField[],
  
  viewFields: [
    {
      name: 'id',
      label: 'ID',
      type: 'text',
      gridSize: { xs: 12, md: 6 }
    },
    {
      name: 'name',
      label: 'Customer Name',
      type: 'text',
      gridSize: { xs: 12, md: 6 }
    },
    {
      name: 'address_1',
      label: 'Address Line 1',
      type: 'text',
      gridSize: { xs: 12 }
    },
    {
      name: 'address_2',
      label: 'Address Line 2',
      type: 'text',
      gridSize: { xs: 12 }
    },
    {
      name: 'address_3',
      label: 'Address Line 3',
      type: 'text',
      gridSize: { xs: 12 }
    },
    {
      name: 'address_4',
      label: 'Address Line 4',
      type: 'text',
      gridSize: { xs: 12 }
    },
    {
      name: 'pin',
      label: 'PIN Code',
      type: 'text',
      gridSize: { xs: 12, md: 6 }
    },
    {
      name: 'tel',
      label: 'Telephone',
      type: 'text',
      gridSize: { xs: 12, md: 6 }
    },
    {
      name: 'fax',
      label: 'Fax',
      type: 'text',
      gridSize: { xs: 12, md: 6 }
    },
    {
      name: 'mobile',
      label: 'Mobile',
      type: 'text',
      gridSize: { xs: 12, md: 6 }
    },
    {
      name: 'panno',
      label: 'PAN Number',
      type: 'text',
      gridSize: { xs: 12, md: 6 }
    },
    {
      name: 'gst_id',
      label: 'GST Number',
      type: 'text',
      gridSize: { xs: 12, md: 6 }
    },
    {
      name: 'localoutside',
      label: 'GST Type',
      type: 'text',
      gridSize: { xs: 12, md: 6 }
    },
    {
      name: 'updated_on',
      label: 'Last Updated',
      type: 'text',
      gridSize: { xs: 12, md: 6 }
    }
  ] as ManagementFormField[],
  
  features: {
    create: true,
    edit: true,
    delete: true,
    view: true,
    export: true,
    bulkDelete: true,
    search: true,
    sort: true,
    pagination: true
  },
  
  validation: {
    create: (data: CreateCustomerData) => {
      const errors: Record<string, string> = {};
      if (!data.name?.trim()) {
        errors.name = 'Customer name is required';
      }
      if (!data.address_1?.trim()) {
        errors.address_1 = 'Address line 1 is required';
      }
      if (!data.pin?.toString().trim()) {
        errors.pin = 'PIN code is required';
      }
      if (!data.tel?.toString().trim()) {
        errors.tel = 'Telephone is required';
      }
      if (!data.mobile?.toString().trim()) {
        errors.mobile = 'Mobile is required';
      }
      if (!data.panno?.trim()) {
        errors.panno = 'PAN number is required';
      }
      if (!data.gst_id?.trim()) {
        errors.gst_id = 'GST number is required';
      }
      if (!data.localoutside) {
        errors.localoutside = 'GST type is required';
      }
      if (data.panno && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(data.panno)) {
        errors.panno = 'PAN number must be in format: ABCDE1234F';
      }
      if (data.gst_id && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(data.gst_id)) {
        errors.gst_id = 'GST number must be in valid format';
      }
      return errors;
    },
    edit: (data: UpdateCustomerData) => {
      const errors: Record<string, string> = {};
      if (data.name !== undefined && !data.name.trim()) {
        errors.name = 'Customer name is required';
      }
      if (data.address_1 !== undefined && !data.address_1.trim()) {
        errors.address_1 = 'Address line 1 is required';
      }
      if (data.pin !== undefined && !data.pin.toString().trim()) {
        errors.pin = 'PIN code is required';
      }
      if (data.tel !== undefined && !data.tel.toString().trim()) {
        errors.tel = 'Telephone is required';
      }
      if (data.mobile !== undefined && !data.mobile.toString().trim()) {
        errors.mobile = 'Mobile is required';
      }
      if (data.panno !== undefined && !data.panno.trim()) {
        errors.panno = 'PAN number is required';
      }
      if (data.gst_id !== undefined && !data.gst_id.trim()) {
        errors.gst_id = 'GST number is required';
      }
      if (data.localoutside !== undefined && !data.localoutside) {
        errors.localoutside = 'GST type is required';
      }
      if (data.panno && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(data.panno)) {
        errors.panno = 'PAN number must be in format: ABCDE1234F';
      }
      if (data.gst_id && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(data.gst_id)) {
        errors.gst_id = 'GST number must be in valid format';
      }
      return errors;
    }
  },
  
  ui: {}
};

// Product Management Configuration
export const productManagementConfig: ManagementConfig<Product, CreateProductData, UpdateProductData> = {
  entityName: 'Product',
  entityNamePlural: 'Products',
  routePath: '/products',
  
  service: {
    getItems: ProductService.getProducts,
    getItem: async (id: string) => ({ success: true, data: {} as Product }),
    createItem: ProductService.createProduct,
    updateItem: ProductService.updateProduct,
    deleteItem: async (id: string) => {
      const response = await ProductService.deleteProduct(id);
      return { success: response.success, data: { deletedCount: 1 } };
    },
    bulkDeleteItems: ProductService.bulkDeleteProducts,
    exportItems: ProductService.exportProducts
  },
  
  title: 'Product Management',
  subtitle: 'Manage your product catalog with full CRUD operations',
  
  defaultSortBy: 'item_name',
  defaultSortOrder: 'asc',
  
  // Search fields for client-side filtering
  searchFields: ['item_code', 'item_name', 'hsn_code'],
  
  columns: [
    {
      id: 'item_code',
      label: 'Item Code',
      minWidth: 120,
      sortable: true
    },
    {
      id: 'item_name',
      label: 'Item Name',
      minWidth: 200,
      sortable: true
    },
    {
      id: 'hsn_code',
      label: 'HSN Code',
      minWidth: 120,
      align: 'center',
      sortable: true
    },
    {
      id: 'rate',
      label: 'Rate (₹)',
      minWidth: 100,
      align: 'right',
      sortable: true,
      format: (value: any) => value != null ? `₹${Number(value).toFixed(2)}` : '—'
    },
    {
      id: 'unit',
      label: 'Unit',
      minWidth: 80,
      align: 'center',
      sortable: true
    },
    {
      id: 'gst_percentage',
      label: 'GST %',
      minWidth: 80,
      align: 'center',
      sortable: true,
      format: (value: any) => `${value}%`
    },
    {
      id: 'updated_on',
      label: 'Last Updated',
      minWidth: 120,
      align: 'center',
      format: (value: any) => {
        if (!value) return '-';
        return new Date(value).toLocaleDateString();
      }
    }
  ] as TableColumn<Product>[],
  
  actions: [
    {
      label: 'View',
      icon: 'ViewIcon',
      color: 'info'
    },
    {
      label: 'Edit',
      icon: 'EditIcon',
      color: 'primary'
    },
    {
      label: 'Delete',
      icon: 'DeleteIcon',
      color: 'error'
    }
  ] as TableAction<Product>[],
  
  createFormFields: [
    {
      name: 'item_code',
      label: 'Item Code',
      type: 'uppercase',
      required: true,
      gridSize: { xs: 12, sm: 6 },
      placeholder: 'Enter item code'
    },
    {
      name: 'item_name',
      label: 'Item Name',
      type: 'text',
      required: true,
      gridSize: { xs: 12, sm: 6 },
      placeholder: 'Enter item name'
    },
    {
      name: 'hsn_code',
      label: 'HSN Code',
      type: 'number',
      required: true,
      gridSize: { xs: 12, sm: 6 },
      inputProps: { min: 0 },
      placeholder: 'Enter HSN code'
    },
    {
      name: 'rate',
      label: 'Rate (₹)',
      type: 'currency',
      required: true,
      gridSize: { xs: 12, sm: 6 },
      placeholder: '0.00'
    },
    {
      name: 'unit',
      label: 'Unit',
      type: 'text',
      required: true,
      gridSize: { xs: 12, sm: 6 },
      placeholder: 'pc',
      helperText: 'Default unit is "pc"'
    },
    {
      name: 'gst_percentage',
      label: 'GST Percentage',
      type: 'number',
      required: true,
      gridSize: { xs: 12, sm: 6 },
      inputProps: { min: 0, max: 100, step: 0.01 },
      helperText: 'Default value is 18%',
      placeholder: '18'
    }
  ] as ManagementFormField[],
  
  editFormFields: [
    {
      name: 'item_code',
      label: 'Item Code',
      type: 'uppercase',
      required: true,
      gridSize: { xs: 12, sm: 6 },
      placeholder: 'Enter item code'
    },
    {
      name: 'item_name',
      label: 'Item Name',
      type: 'text',
      required: true,
      gridSize: { xs: 12, sm: 6 },
      placeholder: 'Enter item name'
    },
    {
      name: 'type',
      label: 'Product Type',
      type: 'select',
      required: true,
      gridSize: { xs: 12, sm: 6 },
      options: [
        { value: 'PRODUCT', label: 'Product' },
        { value: 'SERVICE', label: 'Service' }
      ]
    },
    {
      name: 'hsn_code',
      label: 'HSN Code',
      type: 'number',
      required: true,
      gridSize: { xs: 12, sm: 6 },
      inputProps: { min: 0 },
      placeholder: 'Enter HSN code'
    },
    {
      name: 'rate',
      label: 'Rate (₹)',
      type: 'currency',
      required: true,
      gridSize: { xs: 12, sm: 6 },
      placeholder: '0.00'
    },
    {
      name: 'unit',
      label: 'Unit',
      type: 'text',
      required: true,
      gridSize: { xs: 12, sm: 6 },
      placeholder: 'pc'
    },
    {
      name: 'gst_percentage',
      label: 'GST Percentage',
      type: 'number',
      required: true,
      gridSize: { xs: 12, sm: 6 },
      inputProps: { min: 0, max: 100, step: 0.01 },
      placeholder: '18'
    }
  ] as ManagementFormField[],
  
  viewFields: [
    {
      name: 'id',
      label: 'ID',
      type: 'text',
      gridSize: { xs: 12, md: 6 }
    },
    {
      name: 'item_code',
      label: 'Item Code',
      type: 'text',
      gridSize: { xs: 12, md: 6 }
    },
    {
      name: 'item_name',
      label: 'Item Name',
      type: 'text',
      gridSize: { xs: 12, md: 6 }
    },
    {
      name: 'hsn_code',
      label: 'HSN Code',
      type: 'text',
      gridSize: { xs: 12, md: 6 }
    },
    {
      name: 'rate',
      label: 'Rate',
      type: 'text',
      gridSize: { xs: 12, md: 6 }
    },
    {
      name: 'unit',
      label: 'Unit',
      type: 'text',
      gridSize: { xs: 12, md: 6 }
    },
    {
      name: 'gst_percentage',
      label: 'GST Percentage',
      type: 'text',
      gridSize: { xs: 12, md: 6 }
    },
    {
      name: 'updated_on',
      label: 'Last Updated',
      type: 'text',
      gridSize: { xs: 12, md: 6 }
    }
  ] as ManagementFormField[],
  
  features: {
    create: true,
    edit: true,
    delete: true,
    view: true,
    export: true,
    bulkDelete: true,
    search: true,
    sort: true,
    pagination: true
  },
  
  validation: {
    create: (data: CreateProductData) => {
      const errors: Record<string, string> = {};
      if (!data.item_code?.trim()) {
        errors.item_code = 'Item code is required';
      }
      if (!data.item_name?.trim()) {
        errors.item_name = 'Item name is required';
      }
      if (data.hsn_code <= 0) {
        errors.hsn_code = 'HSN code must be greater than 0';
      }
      if (data.rate <= 0) {
        errors.rate = 'Rate must be greater than 0';
      }
      if (!data.unit?.trim()) {
        errors.unit = 'Unit is required';
      }
      if (data.gst_percentage < 0 || data.gst_percentage > 100) {
        errors.gst_percentage = 'GST percentage must be between 0 and 100';
      }
      return errors;
    },
    edit: (data: UpdateProductData) => {
      const errors: Record<string, string> = {};
      if (data.item_code && !data.item_code.trim()) {
        errors.item_code = 'Item code is required';
      }
      if (data.item_name && !data.item_name.trim()) {
        errors.item_name = 'Item name is required';
      }
      if (data.hsn_code && data.hsn_code <= 0) {
        errors.hsn_code = 'HSN code must be greater than 0';
      }
      if (data.rate && data.rate <= 0) {
        errors.rate = 'Rate must be greater than 0';
      }
      if (data.unit && !data.unit.trim()) {
        errors.unit = 'Unit is required';
      }
      if (data.gst_percentage && (data.gst_percentage < 0 || data.gst_percentage > 100)) {
        errors.gst_percentage = 'GST percentage must be between 0 and 100';
      }
      return errors;
    }
  },
  
  ui: {}
};

// User Management Configuration
export const userManagementConfig: ManagementConfig<User, CreateUserData, UpdateUserData> = {
  entityName: 'User',
  entityNamePlural: 'Users',
  routePath: '/users',
  
  service: {
    getItems: UserService.getUsers,
    getItem: async (id: string) => ({ success: true, data: {} as User }),
    createItem: UserService.createUser,
    updateItem: UserService.updateUser,
    deleteItem: async (id: string) => {
      const response = await UserService.deleteUser(id);
      return { success: response.success, data: { deletedCount: 1 } };
    },
    bulkDeleteItems: UserService.bulkDeleteUsers,
    exportItems: async () => new Blob()
  },
  
  title: 'User Management',
  subtitle: 'Manage system users and their permissions',
  
  defaultSortBy: 'username',
  defaultSortOrder: 'asc',
  
  // Search fields for client-side filtering
  searchFields: ['username', 'email', 'role'],
  
  columns: [
    {
      id: 'username',
      label: 'Username',
      minWidth: 150,
      sortable: true
    },
    {
      id: 'email',
      label: 'Email',
      minWidth: 200,
      sortable: true
    },
    {
      id: 'role',
      label: 'Role',
      minWidth: 100,
      align: 'center',
      sortable: true,
      format: (value: any) => {
        const color = value === 'admin' ? 'error' : 'primary';
        return { label: value, color, size: 'small', className: 'status-chip' };
      }
    },
    {
      id: 'createdAt',
      label: 'Created',
      minWidth: 120,
      align: 'center',
      format: (value: any) => new Date(value).toLocaleDateString()
    }
  ] as TableColumn<User>[],
  
  actions: [
    {
      label: 'View',
      icon: 'ViewIcon',
      onClick: () => {},
      color: 'info'
    },
    {
      label: 'Edit',
      icon: 'EditIcon',
      onClick: () => {},
      color: 'primary'
    },
    {
      label: 'Delete',
      icon: 'DeleteIcon',
      onClick: () => {},
      color: 'error'
    }
  ] as TableAction<User>[],
  
  createFormFields: [
    {
      name: 'username',
      label: 'Username',
      type: 'text',
      required: true,
      gridSize: { xs: 12, md: 6 }
    },
    {
      name: 'email',
      label: 'Email',
      type: 'email',
      required: true,
      gridSize: { xs: 12, md: 6 }
    },
    {
      name: 'password',
      label: 'Password',
      type: 'password',
      required: true,
      gridSize: { xs: 12, md: 6 }
    },
    {
      name: 'role',
      label: 'Role',
      type: 'select',
      required: true,
      gridSize: { xs: 12, md: 6 },
      options: [
        { value: 'user', label: 'User' },
        { value: 'admin', label: 'Admin' }
      ]
    }
  ] as ManagementFormField[],
  
  editFormFields: [
    {
      name: 'username',
      label: 'Username',
      type: 'text',
      required: true,
      gridSize: { xs: 12, md: 6 }
    },
    {
      name: 'email',
      label: 'Email',
      type: 'email',
      required: true,
      gridSize: { xs: 12, md: 6 }
    },
    {
      name: 'password',
      label: 'Password',
      type: 'password',
      required: false,
      gridSize: { xs: 12, md: 6 },
      helperText: 'Leave blank to keep current password'
    },
    {
      name: 'role',
      label: 'Role',
      type: 'select',
      required: true,
      gridSize: { xs: 12, md: 6 },
      options: [
        { value: 'user', label: 'User' },
        { value: 'admin', label: 'Admin' }
      ]
    }
  ] as ManagementFormField[],
  
  viewFields: [
    {
      name: 'username',
      label: 'Username',
      type: 'text',
      gridSize: { xs: 12, md: 6 }
    },
    {
      name: 'email',
      label: 'Email',
      type: 'text',
      gridSize: { xs: 12, md: 6 }
    },
    {
      name: 'role',
      label: 'Role',
      type: 'text',
      gridSize: { xs: 12, md: 6 }
    },
    {
      name: 'createdAt',
      label: 'Created',
      type: 'text',
      gridSize: { xs: 12, md: 6 }
    },
    {
      name: 'updatedAt',
      label: 'Last Updated',
      type: 'text',
      gridSize: { xs: 12, md: 6 }
    }
  ] as ManagementFormField[],
  
  features: {
    create: true,
    edit: true,
    delete: true,
    view: true,
    export: false,
    bulkDelete: true,
    search: true,
    sort: true,
    pagination: true
  },
  
  validation: {
    create: (data: CreateUserData) => {
      const errors: Record<string, string> = {};
      if (!data.username?.trim()) {
        errors.username = 'Username is required';
      }
      if (!data.email?.trim()) {
        errors.email = 'Email is required';
      } else if (!/\S+@\S+\.\S+/.test(data.email)) {
        errors.email = 'Please enter a valid email address';
      }
      if (!data.password?.trim()) {
        errors.password = 'Password is required for new users';
      } else if (data.password.length < 6) {
        errors.password = 'Password must be at least 6 characters long';
      }
      return errors;
    },
    edit: (data: UpdateUserData) => {
      const errors: Record<string, string> = {};
      if (!data.username?.trim()) {
        errors.username = 'Username is required';
      }
      if (!data.email?.trim()) {
        errors.email = 'Email is required';
      } else if (!/\S+@\S+\.\S+/.test(data.email)) {
        errors.email = 'Please enter a valid email address';
      }
      return errors;
    }
  },
  
  ui: {}
};

// Invoice Management Configuration
export const invoiceManagementConfig: ManagementConfig<Invoice, CreateInvoiceData, UpdateInvoiceData> = {
  entityName: 'Invoice',
  entityNamePlural: 'Invoices',
  routePath: '/invoices',
  
  service: {
    getItems: () => BillService.getBills(),
    getItem: BillService.getBillById,
    createItem: BillService.createBill,
    updateItem: BillService.updateBill,
    deleteItem: async (id: string) => {
      const response = await BillService.deleteBill(id);
      return { success: response.success, data: { deletedCount: 1 } };
    },
    bulkDeleteItems: BillService.bulkDeleteBills,
    exportItems: () => BillService.exportBills()
  },
  
  title: 'Invoice Management',
  subtitle: 'Manage your invoice database with full CRUD operations',
  
  defaultSortBy: 'invoice_number',
  defaultSortOrder: 'desc',
  
  // Search fields for client-side filtering
  searchFields: ['invoice_number'],

  createRoute: '/create-invoice',

  columns: [
    {
      id: 'invoice_number',
      label: 'Invoice Number',
      minWidth: 150,
      sortable: true
    },
    {
      id: 'customer_name',
      label: 'Customer Name',
      minWidth: 200,
      sortable: true
    },
    {
      id: 'invoice_date',
      label: 'Invoice Date',
      minWidth: 120,
      align: 'center',
      sortable: true,
      format: (value: any) => new Date(value).toLocaleDateString()
    },
    {
      id: 'gross_amount',
      label: 'Total Amount (₹)',
      minWidth: 120,
      align: 'right',
      sortable: true,
      format: (value: any) => value != null ? `₹${Number(value).toFixed(2)}` : '—'
    },
    {
      id: 'created_by',
      label: 'Created By',
      minWidth: 120,
      format: (value: any) => value || '-'
    },
    {
      id: 'updated_on',
      label: 'Last Updated',
      minWidth: 120,
      align: 'center',
      format: (value: any) => {
        if (!value) return '-';
        return new Date(value).toLocaleDateString();
      }
    }
  ] as TableColumn<Invoice>[],
  
  actions: [
    {
      label: 'View',
      icon: 'ViewIcon',
      onClick: (invoice: Invoice) => {
        // Navigate to create invoice page in edit mode
        window.location.href = `/create-invoice?edit=${invoice._id}`;
      },
      color: 'info'
    },
    {
      label: 'Edit',
      icon: 'EditIcon',
      onClick: (invoice: Invoice) => {
        // Navigate to create invoice page in edit mode
        window.location.href = `/create-invoice?edit=${invoice._id}`;
      },
      color: 'primary'
    },
    {
      label: 'Print',
      icon: 'PrintIcon',
      onClick: (invoice: Invoice) => {
        // Navigate to print page with invoice ID
        window.open(`/print/${invoice._id}`, '_blank');
      },
      color: 'secondary'
    },
    {
      label: 'Delete',
      icon: 'DeleteIcon',
      color: 'error'
    }
  ] as TableAction<Invoice>[],
  
  createFormFields: [
    {
      name: 'invoice_number',
      label: 'Invoice Number',
      type: 'text',
      required: true,
      gridSize: { xs: 12, md: 6 }
    },
    {
      name: 'customer_name',
      label: 'Customer Name',
      type: 'text',
      required: true,
      gridSize: { xs: 12, md: 6 }
    },
    {
      name: 'invoice_date',
      label: 'Invoice Date',
      type: 'date',
      required: true,
      gridSize: { xs: 12, md: 6 }
    },
    {
      name: 'gross_amount',
      label: 'Total Amount (₹)',
      type: 'number',
      required: true,
      gridSize: { xs: 12, md: 6 },
      inputProps: { min: 0, step: 0.01 }
    },
    {
      name: 'status',
      label: 'Status',
      type: 'select',
      required: true,
      gridSize: { xs: 12, md: 6 },
      options: [
        { value: 'Draft', label: 'Draft' },
        { value: 'Pending', label: 'Pending' },
        { value: 'Paid', label: 'Paid' },
        { value: 'Cancelled', label: 'Cancelled' }
      ]
    },
    {
      name: 'created_by',
      label: 'Created By',
      type: 'text',
      required: true,
      gridSize: { xs: 12, md: 6 }
    }
  ] as ManagementFormField[],
  
  editFormFields: [
    {
      name: 'invoice_number',
      label: 'Invoice Number',
      type: 'text',
      required: true,
      gridSize: { xs: 12, md: 6 }
    },
    {
      name: 'customer_name',
      label: 'Customer Name',
      type: 'text',
      required: true,
      gridSize: { xs: 12, md: 6 }
    },
    {
      name: 'invoice_date',
      label: 'Invoice Date',
      type: 'date',
      required: true,
      gridSize: { xs: 12, md: 6 }
    },
    {
      name: 'gross_amount',
      label: 'Total Amount (₹)',
      type: 'number',
      required: true,
      gridSize: { xs: 12, md: 6 },
      inputProps: { min: 0, step: 0.01 }
    },
    {
      name: 'status',
      label: 'Status',
      type: 'select',
      required: true,
      gridSize: { xs: 12, md: 6 },
      options: [
        { value: 'Draft', label: 'Draft' },
        { value: 'Pending', label: 'Pending' },
        { value: 'Paid', label: 'Paid' },
        { value: 'Cancelled', label: 'Cancelled' }
      ]
    }
  ] as ManagementFormField[],
  
  viewFields: [
    {
      name: 'id',
      label: 'ID',
      type: 'text',
      gridSize: { xs: 12, md: 6 }
    },
    {
      name: 'invoice_number',
      label: 'Invoice Number',
      type: 'text',
      gridSize: { xs: 12, md: 6 }
    },
    {
      name: 'customer_name',
      label: 'Customer Name',
      type: 'text',
      gridSize: { xs: 12, md: 6 }
    },
    {
      name: 'invoice_date',
      label: 'Invoice Date',
      type: 'text',
      gridSize: { xs: 12, md: 6 }
    },
    {
      name: 'gross_amount',
      label: 'Total Amount',
      type: 'text',
      gridSize: { xs: 12, md: 6 }
    },
    {
      name: 'status',
      label: 'Status',
      type: 'text',
      gridSize: { xs: 12, md: 6 }
    },
    {
      name: 'created_by',
      label: 'Created By',
      type: 'text',
      gridSize: { xs: 12, md: 6 }
    },
    {
      name: 'updated_by',
      label: 'Updated By',
      type: 'text',
      gridSize: { xs: 12, md: 6 }
    },
    {
      name: 'created_on',
      label: 'Created On',
      type: 'text',
      gridSize: { xs: 12, md: 6 }
    },
    {
      name: 'updated_on',
      label: 'Last Updated',
      type: 'text',
      gridSize: { xs: 12, md: 6 }
    }
  ] as ManagementFormField[],
  
  features: {
    create: true,
    edit: true,
    delete: true,
    view: true,
    export: true,
    bulkDelete: true,
    search: true,
    sort: true,
    pagination: true
  },
  
  validation: {
    create: (data: CreateInvoiceData) => {
      const errors: Record<string, string> = {};
      if (!data.invoice_number?.trim()) {
        errors.invoice_number = 'Invoice number is required';
      } else if (!/^\d+\/\d{2}-\d{2}$/.test(data.invoice_number)) {
        errors.invoice_number = 'Invoice number must be in format: 123/24-25';
      }
      if (!data.customer_name?.trim()) {
        errors.customer_name = 'Customer name is required';
      }
      if (!data.invoice_date) {
        errors.invoice_date = 'Invoice date is required';
      }
      if (data.gross_amount <= 0) {
        errors.gross_amount = 'Total amount must be greater than 0';
      }
      if (!data.status?.trim()) {
        errors.status = 'Status is required';
      }
      if (!data.created_by?.trim()) {
        errors.created_by = 'Created by field is required';
      }
      return errors;
    },
    edit: (data: UpdateInvoiceData) => {
      const errors: Record<string, string> = {};
      if (data.invoice_number && !data.invoice_number.trim()) {
        errors.invoice_number = 'Invoice number is required';
      } else if (data.invoice_number && !/^\d+\/\d{2}-\d{2}$/.test(data.invoice_number)) {
        errors.invoice_number = 'Invoice number must be in format: 123/24-25';
      }
      if (data.customer_name && !data.customer_name.trim()) {
        errors.customer_name = 'Customer name is required';
      }
      if (data.invoice_date && !data.invoice_date) {
        errors.invoice_date = 'Invoice date is required';
      }
      if (data.gross_amount && data.gross_amount <= 0) {
        errors.gross_amount = 'Total amount must be greater than 0';
      }
      if (data.status && !data.status.trim()) {
        errors.status = 'Status is required';
      }
      return errors;
    }
  },
  
  ui: {}
};

// Export all configurations
export const managementConfigs = {
  customer: customerManagementConfig,
  product: productManagementConfig,
  user: userManagementConfig,
  invoice: invoiceManagementConfig
};

// Helper function to get config by entity type
export const getManagementConfig = (entityType: string) => {
  return managementConfigs[entityType as keyof typeof managementConfigs];
}; 